import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomSelect from "components/Common/custom-select";
import CustomDatePicker from "common/CustomDatePicker";
import TableContainer from "components/Common/TableContainer";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import { fetchCategories, fetchDepartments, fetchExemptionResignedReport, resetEmployeeReport } from "store/actions";
import { getAttendanceSalaryDate } from "common/attendanceSalaryDate";

const EmployeeReportExemption = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
      const rights = getAccessRights(location.pathname, history);
      setAccessRight(rights?.data);
  }, [location]);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);
  const {
    employeeReport,
    categories,
    departments,
    loading,
    error,
    success
  } = useSelector((state) => ({
    employeeReport: state.EmployeeReportReducer?.employeeReport ?? [],
    categories: state.CategoryMaster?.categories ?? [],
    departments: state.DepartmentMaster?.departments ?? [],

    loading: state.EmployeeReportReducer?.loading,
    error: state.EmployeeReportReducer?.error,
    success: state.EmployeeReportReducer?.success,
  }));

  useEffect(() => {
      if (authDetails) {
        getAttendanceSalaryDate(authDetails.unit_id).then(response => {
          if (response.status === "success" && response.data.length > 0) {
            const data = response.data[0];
            formik.setFieldValue("fromDate", data.from_date.split('/').reverse().join('-'));
            formik.setFieldValue("toDate", data.to_date.split('/').reverse().join('-'));
          }
        });
      }
    }, [authDetails]);

  useEffect(() => {
    dispatch(resetEmployeeReport())
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchCategories(unitId));
    dispatch(fetchDepartments(unitId));
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      category: "",
      department: "",
      code: "",
      fromDate: "",
      toDate: "",
      statusType: "Exemption",
    },

    validationSchema: Yup.object({
       fromDate: Yup.date()
          .nullable(),
          
      toDate: Yup.date()
          .nullable()
          .when("fromDate", (fromDate, schema) => {
            return fromDate 
              ? schema.min(fromDate, "To Date must be greater than or equal to From Date")
              : schema;
        }),
    }),

    onSubmit: (values) => {
      // console.log("Form values:", values);
      const formData = new FormData();
      
      formData.append("emp_cat", values.category || 0);
      formData.append("emp_code", 0);
      formData.append("emp_dep", values.department || 0);
      formData.append("unit_id", authDetails?.unit_id);

      formData.append("from_date", values.fromDate);
      formData.append("to_date", values.toDate);

      const typeValue = values.statusType === "Exemption" ? 1 : 2;
      formData.append("Opt", typeValue);

      dispatch(fetchExemptionResignedReport(formData));
    },
  });


  // const columns = [
  //   { Header: "S.No", Cell: ({ row }) => employeeReport.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
  //   { Header: "Code", accessor: "emp_code" },
  //   { Header: "Name", accessor: "name" },
  //   { Header: "Department", accessor: "department" },
  //   { Header: "Effective Date", accessor: "effective_date" },
  //   { Header: "Reason", accessor: "reason" },
  //   { Header: "Updated By", accessor: "updated_by" },
  //   { Header: "Updated On", accessor: "updated_on" },
  // ];

  const getColumnsByStatus = (type) => {
    const baseColumns = [
      { Header: "S.No", Cell: ({ row }) => employeeReport.length - row.index, className: "text-center" },
      { Header: "Code", accessor: "emp_code" },
      { Header: "Name", accessor: "name" },
      { Header: "Category", accessor: "category" },      
      { Header: "Department", accessor: "department" },      
    ];

    if (type === "Resigned") {
      const resignedColumns = [
        { Header: "Joined", accessor: "joined_date" },
        { Header: "Resigned", accessor: "effective_date" },
        { Header: "Accepted", accessor: "accepted_dt" },
        { Header: "Notice", accessor: "notice_period" },
        { Header: "Relieved", accessor: "relieve_dt" },        
        { Header: "Age", accessor: "age" },
        { Header: "Experience", accessor: "experience" },
        { Header: "Status", accessor: "status" },
        { Header: "Reason", accessor: "reason" },
        { Header: "Updated By", accessor: "updated_by" },
        { Header: "Updated On", accessor: "updated_on" },
      ];
      return [...baseColumns, ...resignedColumns];
    }

    // Default type -> Exemption
    const exemptionColumns = [
      { Header: "Effective Date", accessor: "effective_date" },
      { Header: "Reason", accessor: "reason" },      
      { Header: "Updated By", accessor: "updated_by" },
      { Header: "Updated On", accessor: "updated_on" },
    ];

    return [...baseColumns, ...exemptionColumns];
  };

  const columns = useMemo(
    () => getColumnsByStatus(formik.values.statusType),
    [formik.values.statusType, employeeReport]
  );

  const handleReset = () => {
      formik.resetForm(); 
      dispatch(resetEmployeeReport()); 
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Employee" breadcrumbItem="Exemption / Resigned Details" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3 ">
                <Col md={3}>
                  <Label>Category</Label>
                  <CustomSelect
                    id="category"
                    name="category"
                    options={categories.map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))}
                    value={
                      categories
                        .map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))
                        .find(cat => cat.value === formik.values.category) || null
                    }
                    onChange={val => formik.setFieldValue('category', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.category && formik.errors.category ? 'is-invalid' : ''}`}
                  />
                </Col>                

                <Col md={3}>
                  <Label>Department</Label>
                  <CustomSelect
                      id="department"
                      name="department"
                      options={departments.map(dept => ({ value: dept.departmentId, label: dept.departmentname }))}
                      value={
                        departments
                          .map(dept => ({ value: dept.departmentId, label: dept.departmentname }))
                          .find(dept => dept.value === formik.values.department) || null
                      }
                      onChange={val => formik.setFieldValue('department', val ? val.value : '')}
                      onBlur={formik.handleBlur}
                      className={`select-dropdown ${formik.touched.department && formik.errors.department ? 'is-invalid' : ''}`}
                    />
                </Col>

                {/* <Col md={4}>
                  <Label>Code</Label>
                  <Input
                    type="text"
                    name="code"
                    value={formik.values.code}
                    onChange={formik.handleChange}
                  />
                </Col> */}

                <Col md={3}>
                  <Label>From Date </Label>
                  <CustomDatePicker
                    name="fromDate"
                    value={formik.values.fromDate}
                    onChange={(val) => formik.setFieldValue("fromDate", val)}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.fromDate && formik.errors.fromDate ? "is-invalid" : ""}`}
                  />
                  {formik.touched.fromDate && formik.errors.fromDate && (
                    <div className="text-danger">{formik.errors.fromDate}</div>
                  )}
                </Col>

                <Col md={3}>
                  <Label>To Date </Label>
                  <CustomDatePicker
                    name="toDate"
                    value={formik.values.toDate}
                    onChange={(val) => formik.setFieldValue("toDate", val)}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.toDate && formik.errors.toDate ? "is-invalid" : ""}`}
                  />
                  {formik.touched.toDate && formik.errors.toDate && (
                    <div className="text-danger">{formik.errors.toDate}</div>
                  )}
                </Col>  

                <Col md={3}>
                  <Label></Label>
                  <div className="d-flex align-items-center gap-4 mt-2">
                    <Label className="mb-0">
                      <Input
                        type="radio"
                        name="statusType"
                        value="Exemption"
                        checked={formik.values.statusType === "Exemption"}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                          formik.handleChange(e);
                          dispatch(resetEmployeeReport());
                        }}
                      /> Exemption
                    </Label>
                    <Label className="mb-0">
                      <Input
                        type="radio"
                        name="statusType"
                        value="Resigned"
                        checked={formik.values.statusType === "Resigned"}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                          formik.handleChange(e);
                          dispatch(resetEmployeeReport());
                        }}
                      /> Resigned
                    </Label>
                  </div>
                </Col>

                <Col className="d-flex mob-center align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2 mt-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" className="btn button-cancel me-2 mt-2" onClick={handleReset}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {employeeReport && employeeReport.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export mt-2">
                        <i className="fas fa-file-export me-1" /> Export
                      </DropdownToggle>

                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "ExemptionResigned Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>

                        <DropdownItem onClick={() => exportEmployeeToPDF(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "ExemptionResigned Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}  
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={employeeReport}
              isGlobalFilter={true}
              customPageSize={10}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default EmployeeReportExemption;
